import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "../../ui/button";
import { useScopedBoxes } from "../../../hooks/useScopedBoxes";
import {
  createUserChannel,
  createUserPreference,
  deleteUserChannel,
  deleteUserPreference,
  fetchUserChannels,
  fetchUserPreferences,
  updateUserPreference,
} from "../../../services/adminApi";
import {
  DOW_BITS,
  dowMaskLabel,
  timeLabel,
  type AlertType,
  type Cadence,
  type NotificationChannel,
  type NotificationPreference,
} from "../../../services/notificationsApi";

const NOTIF_HOURS = Array.from({ length: 24 }, (_, i) => i);
const NOTIF_MINUTES = [0, 15, 30, 45];

/** Human schedule summary for a saved preference (mirrors NotificationsTab). */
function prefScheduleLabel(p: NotificationPreference): string {
  if (p.cadence === "immediate") return "Immediately (per checkout)";
  if (p.cadence === "monthly") {
    return `Monthly on day ${p.day_of_month} at ${timeLabel(p.hour_local, p.minute_local)}`;
  }
  return `${dowMaskLabel(p.dow_mask)} at ${timeLabel(p.hour_local, p.minute_local)}`;
}

/**
 * Lets an admin manage a chosen user's notification channels + subscriptions.
 * Self-contained: loads the target user's channels + prefs, adds an email
 * channel, and adds a refill / manual-checkout subscription (with a SmartBox
 * picker + cadence + day-of-month for manual checkout). All writes go through
 * the target-scoped /api/admin/users/{uid}/notifications/* routes; the server
 * re-checks users.manage + company scope + the target's SCU access.
 */
export const UserNotificationsSection: React.FC<{
  uid: number;
  token: string;
  effectiveScus: string[];
}> = ({ uid, token, effectiveScus }) => {
  const [channels, setChannels] = useState<NotificationChannel[]>([]);
  const [prefs, setPrefs] = useState<NotificationPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Add-channel (email only — the common case; SMS stays in self-serve).
  const [chAddr, setChAddr] = useState("");

  // Add-subscription form.
  const [alertType, setAlertType] = useState<AlertType>("refill");
  const [scu, setScu] = useState<string | null>(null);
  const [channelId, setChannelId] = useState<number | null>(null);
  const [cadence, setCadence] = useState<Cadence>("daily");
  const [hour, setHour] = useState(9);
  const [minute, setMinute] = useState(0);
  const [dowMask, setDowMask] = useState(127);
  const [dayOfMonth, setDayOfMonth] = useState(1);

  const { snapshot } = useScopedBoxes();
  const boxLabels = useMemo(
    () =>
      new Map(
        (snapshot?.boxes ?? []).map((b) => [
          b.scu_name,
          [b.smartbox_code, b.customer].filter(Boolean).join(" · ") || b.scu_name,
        ]),
      ),
    [snapshot],
  );
  const boxOptions = useMemo(
    () =>
      effectiveScus
        .map((s) => ({ scu: s, label: boxLabels.get(s) ?? s }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [effectiveScus, boxLabels],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [c, p] = await Promise.all([
        fetchUserChannels(uid, token),
        fetchUserPreferences(uid, token),
      ]);
      setChannels(c);
      setPrefs(p);
      setChannelId((prev) =>
        prev !== null && c.find((x) => x.channel_id === prev)
          ? prev
          : (c[0]?.channel_id ?? null),
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [uid, token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onChangeAlertType = (t: AlertType) => {
    setAlertType(t);
    if (t === "refill") {
      setScu(null);
      if (cadence !== "daily" && cadence !== "weekly") setCadence("daily");
    } else {
      setCadence("immediate");
    }
  };

  const toggleDow = (bit: number) =>
    setDowMask((prev) => (prev & bit ? prev & ~bit : prev | bit));

  const onAddChannel = async () => {
    if (!chAddr.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await createUserChannel(uid, { kind: "email", address: chAddr.trim() }, token);
      setChAddr("");
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onDeleteChannel = async (cid: number) => {
    if (!window.confirm("Remove this channel? Any alerts using it will be paused.")) return;
    setErr(null);
    try {
      await deleteUserChannel(uid, cid, token);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const onAddPref = async () => {
    if (channelId === null) {
      setErr("Add an email channel first.");
      return;
    }
    if (alertType === "manual_checkout" && !scu) {
      setErr("Pick a SmartBox for the manual-checkout report.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await createUserPreference(
        uid,
        {
          alert_type: alertType,
          channel_id: channelId,
          cadence,
          ...(cadence !== "immediate" ? { hour_local: hour, minute_local: minute } : {}),
          dow_mask: cadence === "weekly" ? dowMask : 127,
          ...(cadence === "monthly" ? { day_of_month: dayOfMonth } : {}),
          ...(alertType === "manual_checkout" ? { scu } : {}),
        },
        token,
      );
      setAlertType("refill");
      setScu(null);
      setCadence("daily");
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onTogglePref = async (p: NotificationPreference, enabled: boolean) => {
    setErr(null);
    try {
      await updateUserPreference(uid, p.preference_id, { enabled }, token);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const onDeletePref = async (pid: number) => {
    if (!window.confirm("Remove this alert?")) return;
    setErr(null);
    try {
      await deleteUserPreference(uid, pid, token);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  if (loading && channels.length === 0 && prefs.length === 0) {
    return <div className="text-xs text-secondary-500">loading notifications…</div>;
  }

  const selectCls = "border border-secondary-300 rounded px-2 py-1 text-sm bg-white";

  return (
    <div className="space-y-3 text-sm">
      {err && (
        <div className="text-xs text-error-700 bg-error-50 border border-error-200 px-2 py-1 rounded">
          {err}
        </div>
      )}

      {/* Channels */}
      <div className="border border-secondary-200 rounded p-2">
        <div className="text-[11px] uppercase tracking-wide text-secondary-500 mb-1">
          Email channels
        </div>
        {channels.length === 0 ? (
          <p className="text-xs text-secondary-500 mb-1">None yet.</p>
        ) : (
          <ul className="divide-y divide-secondary-100 mb-1">
            {channels.map((c) => (
              <li key={c.channel_id} className="py-1 flex items-center gap-2">
                <span className="text-[10px] uppercase font-semibold text-secondary-500 w-8">
                  {c.kind}
                </span>
                <span className="font-mono text-xs">{c.address}</span>
                <button
                  type="button"
                  onClick={() => void onDeleteChannel(c.channel_id)}
                  className="ml-auto text-xs text-secondary-500 hover:text-error-700"
                >
                  remove
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2 items-center">
          <input
            value={chAddr}
            onChange={(e) => setChAddr(e.target.value)}
            placeholder="add email — you@example.com"
            className="flex-1 border border-secondary-300 rounded px-2 py-1 text-xs font-mono"
          />
          <Button size="sm" variant="outline" disabled={busy || !chAddr.trim()} onClick={() => void onAddChannel()}>
            Add
          </Button>
        </div>
      </div>

      {/* Subscriptions */}
      <div className="border border-secondary-200 rounded p-2">
        <div className="text-[11px] uppercase tracking-wide text-secondary-500 mb-1">
          Subscriptions
        </div>
        {prefs.length === 0 ? (
          <p className="text-xs text-secondary-500 mb-2">None yet.</p>
        ) : (
          <ul className="divide-y divide-secondary-100 mb-2">
            {prefs.map((p) => (
              <li key={p.preference_id} className="py-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">
                    {p.alert_type === "manual_checkout" ? "Manual checkout" : "Refill"}
                  </span>
                  {p.scu && (
                    <span className="text-[11px] font-mono text-secondary-600 bg-secondary-50 border border-secondary-200 rounded px-1 py-0.5">
                      {boxLabels.get(p.scu) ?? p.scu}
                    </span>
                  )}
                  <span className="text-secondary-400">→</span>
                  <span className="font-mono text-xs">{p.channel_address}</span>
                  <label className="ml-auto flex items-center gap-1 text-xs text-secondary-700">
                    <input
                      type="checkbox"
                      checked={p.enabled}
                      onChange={(e) => void onTogglePref(p, e.target.checked)}
                    />
                    {p.enabled ? "on" : "off"}
                  </label>
                  <button
                    type="button"
                    onClick={() => void onDeletePref(p.preference_id)}
                    className="text-xs text-secondary-500 hover:text-error-700"
                  >
                    remove
                  </button>
                </div>
                <div className="text-[11px] text-secondary-500 mt-0.5">
                  {prefScheduleLabel(p)}
                </div>
              </li>
            ))}
          </ul>
        )}

        {channels.length === 0 ? (
          <p className="text-xs text-secondary-500">Add an email channel first to create a subscription.</p>
        ) : (
          <div className="border-t border-secondary-100 pt-2 space-y-2">
            <div className="flex gap-2 items-center flex-wrap">
              <label className="text-secondary-600 text-xs w-16">Alert</label>
              <select
                value={alertType}
                onChange={(e) => onChangeAlertType(e.target.value as AlertType)}
                className={selectCls}
              >
                <option value="refill">Refill reminders</option>
                <option value="manual_checkout">Manual checkout reports</option>
              </select>
            </div>

            {alertType === "manual_checkout" && (
              <div className="flex gap-2 items-center flex-wrap">
                <label className="text-secondary-600 text-xs w-16">SmartBox</label>
                <select
                  value={scu ?? ""}
                  onChange={(e) => setScu(e.target.value || null)}
                  className={selectCls + " max-w-full"}
                >
                  <option value="" disabled>
                    Select a SmartBox…
                  </option>
                  {boxOptions.map((b) => (
                    <option key={b.scu} value={b.scu}>
                      {b.label}
                    </option>
                  ))}
                </select>
                {boxOptions.length === 0 && (
                  <span className="text-[11px] text-secondary-500">
                    (this user has no boxes in scope)
                  </span>
                )}
              </div>
            )}

            <div className="flex gap-2 items-center flex-wrap">
              <label className="text-secondary-600 text-xs w-16">Send to</label>
              <select
                value={channelId ?? ""}
                onChange={(e) => setChannelId(e.target.value === "" ? null : Number(e.target.value))}
                className={selectCls}
              >
                {channels.map((c) => (
                  <option key={c.channel_id} value={c.channel_id}>
                    {c.kind.toUpperCase()} {c.address}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2 items-center flex-wrap">
              <label className="text-secondary-600 text-xs w-16">When</label>
              <select
                value={cadence}
                onChange={(e) => setCadence(e.target.value as Cadence)}
                className={selectCls}
              >
                {alertType === "manual_checkout" && (
                  <option value="immediate">Immediately (per checkout)</option>
                )}
                <option value="daily">Every day</option>
                <option value="weekly">Specific days</option>
                {alertType === "manual_checkout" && <option value="monthly">Monthly</option>}
              </select>
              {cadence !== "immediate" && (
                <>
                  <span className="text-secondary-600 text-xs">at</span>
                  <select value={hour} onChange={(e) => setHour(Number(e.target.value))} className={selectCls}>
                    {NOTIF_HOURS.map((h) => (
                      <option key={h} value={h}>
                        {String(h).padStart(2, "0")}
                      </option>
                    ))}
                  </select>
                  <span>:</span>
                  <select value={minute} onChange={(e) => setMinute(Number(e.target.value))} className={selectCls}>
                    {NOTIF_MINUTES.map((m) => (
                      <option key={m} value={m}>
                        {String(m).padStart(2, "0")}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>

            {cadence === "weekly" && (
              <div className="flex gap-1 items-center flex-wrap">
                <label className="text-secondary-600 text-xs w-16">Days</label>
                {DOW_BITS.map((d) => (
                  <button
                    key={d.bit}
                    type="button"
                    onClick={() => toggleDow(d.bit)}
                    className={
                      "px-2 py-1 rounded text-xs border " +
                      (dowMask & d.bit
                        ? "bg-primary-100 text-primary-800 border-primary-300"
                        : "bg-white text-secondary-600 border-secondary-300 hover:bg-secondary-50")
                    }
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            )}

            {cadence === "monthly" && (
              <div className="flex gap-2 items-center flex-wrap">
                <label className="text-secondary-600 text-xs w-16">Day</label>
                <select
                  value={dayOfMonth}
                  onChange={(e) => setDayOfMonth(Number(e.target.value))}
                  className={selectCls}
                >
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
                <span className="text-[11px] text-secondary-500">(1–28)</span>
              </div>
            )}

            <div className="flex justify-end">
              <Button
                size="sm"
                disabled={
                  busy ||
                  (cadence === "weekly" && dowMask === 0) ||
                  (alertType === "manual_checkout" && !scu)
                }
                onClick={() => void onAddPref()}
              >
                {busy ? "Adding…" : "Add subscription"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
